import { getPiniaStore } from '@/src/plugins/storeRegistry';

type PropKey = number | string;

type RestrictHook = (storeName: string, propPath: PropKey[]) => boolean;

const restrictServerStore: RestrictHook = (storeName) => {
  return storeName === 'server';
};

const restrictPiniaProperties: RestrictHook = (storeName, propPath) => {
  return propPath.includes('_p');
};

const RestrictHooks: RestrictHook[] = [
  restrictServerStore,
  restrictPiniaProperties,
];

function getStoreProperty<T = unknown>(
  storeName: string,
  propPath: PropKey[],
): T {
  if (RestrictHooks.some(hook => hook(storeName, propPath))) {
    throw new Error('Cannot access the requested store and property');
  }

  const store = getPiniaStore(storeName);
  if (!store) {
    throw new Error(`${storeName} does not exist or is not initialized`);
  }

  function lookupKey(obj: unknown, key: PropKey): unknown {
    if (typeof key === 'number' && Array.isArray(obj)) {
      return obj[key];
    }
    if (typeof key === 'string' && typeof obj === 'object' && obj !== null) {
      return (obj as Record<string, unknown>)[key];
    }
    throw new Error(`Cannot lookup key ${key} on object ${obj as any}`);
  }

  let value = store as unknown;
  let prop: PropKey | undefined;
  while ((prop = propPath.shift()) !== undefined) {
    value = lookupKey(value, prop);
  }
  return value as T;
}

async function callStoreMethod(
  storeName: string,
  propPath: PropKey[],
  args: unknown[],
) {
  const method = getStoreProperty<(...args: unknown[]) => unknown>(storeName, propPath);
  return method(...args);
}

export const StoreApi = {
  getStoreProperty,
  callStoreMethod,
};
